import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false, error: null };

  private isModuleLoadError(error: Error) {
    const message = `${error.message || ""} ${error.stack || ""}`.toLowerCase();
    return (
      message.includes("importing a module script failed") ||
      message.includes("failed to fetch dynamically imported module") ||
      message.includes("loading chunk") ||
      message.includes("module script")
    );
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error.message, error.stack);
    console.error("Component stack:", errorInfo.componentStack);

    if (this.isModuleLoadError(error)) {
      const reloadKey = "stylere-module-reload-attempted";
      if (sessionStorage.getItem(reloadKey) !== "1") {
        sessionStorage.setItem(reloadKey, "1");
        const url = new URL(window.location.href);
        url.searchParams.set("v", Date.now().toString());
        window.location.replace(url.toString());
      }
    }
  }

  private handleRetry = () => {
    if (this.state.error && this.isModuleLoadError(this.state.error)) {
      sessionStorage.removeItem("stylere-module-reload-attempted");
      const url = new URL(window.location.href);
      url.searchParams.set("v", Date.now().toString());
      window.location.replace(url.toString());
      return;
    }

    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="text-6xl mb-4">😔</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-6">
              We're sorry for the inconvenience. Please try again.
            </p>
            {this.state.error && (
              <pre className="text-left text-xs bg-red-50 text-red-800 p-3 rounded mb-4 overflow-auto max-h-40">
                {this.state.error.message}
                {"\n"}
                {this.state.error.stack?.split("\n").slice(0, 5).join("\n")}
              </pre>
            )}
            <button
              onClick={this.handleRetry}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
